import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PasswordModal.scss';
import { ProfileEditModal } from './ProfileEditModal';
import { validatePasswordData } from './validation';
import showPasswordIcon from '../../assets/icons/show-password-icon.svg';

interface PasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: Record<string, any>) => Promise<void>;
}

export const PasswordModal: React.FC<PasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Reset form state when modal closes
  const handleClose = () => {
    // Reset password fields
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    
    // Reset visibility states
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    
    // Reset any errors
    setErrors({});
    
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSuccess({
        currentPassword,
        newPassword,
        confirmPassword
      });
      onClose();
    } catch (error) {
      console.error('Error updating password:', error);
    }
    setIsSubmitting(false);
  };

  return (
    <ProfileEditModal
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={t('sections.password.title')}
      validate={(data) => {
        const validationErrors = validatePasswordData(data);
        const errorMap: Record<string, string> = {};
        validationErrors.forEach(error => {
          if (error.field === 'general') {
            // Handle general errors separately
            errorMap.general = error.message;
          } else {
            errorMap[error.field] = error.message;
          }
        });
        setErrors(errorMap);
        return validationErrors;
      }}
      isSubmitting={isSubmitting}
    >
      <div className="form-groups-container">
        {errors.general && (
          <div className="general-error">{errors.general}</div>
        )}
        {/* Current password - full width */}
        <div className="form-group full-width">
          <label htmlFor="currentPassword">
            {t('sections.password.currentPassword')}
          </label>
          <div className="password-input-wrapper">
            <input
              type={showCurrentPassword ? "text" : "password"}
              id="currentPassword"
              name="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className={errors.currentPassword ? 'error' : ''}
            />
            {errors.currentPassword && (
              <div className="error-message">{errors.currentPassword}</div>
            )}
            <button
              type="button"
              className="show-password-button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              aria-label={showCurrentPassword ? "Hide password" : "Show password"}
            >
              <img src={showPasswordIcon} alt="" />
            </button>
          </div>
        </div>

        {/* New password and confirm - side by side */}
        <div className="form-groups-grid">
          <div className="form-group">
            <label htmlFor="newPassword">
              {t('sections.password.newPassword')}
              <span className="requirements">
                {t('sections.password.requirements')}
              </span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className={errors.confirmPassword ? 'error' : ''}
              />
              {errors.confirmPassword && (
                <div className="error-message">{errors.confirmPassword}</div>
              )}
              <button
                type="button"
                className="show-password-button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <img src={showPasswordIcon} alt="" />
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              {t('sections.password.confirmPassword')}
              <span className="requirements">
                {t('sections.password.requirements')}
              </span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={errors.confirmPassword ? 'error' : ''}
              />
              {errors.confirmPassword && (
                <div className="error-message">{errors.confirmPassword}</div>
              )}
              <button
                type="button"
                className="show-password-button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                <img src={showPasswordIcon} alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </ProfileEditModal>
  );
};
